// 引入 React 与常用 Hook。
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
// 引入 RC 环境判断工具。
import { isRC } from '@/utils';
// 引入 className 合并工具。
import cx from 'classnames';
// 引入样式。
import Styles from './index.module.scss';
// 引入 Lottie 预加载与资源解析工具。
import { getLottieObjectUrl, waitForLottiePreload, preloadDotLottieRuntime } from '@/utils/preloadLottie';

// Lottie 源地址请求 Promise 缓存。
const lottieSrcPromiseCache = new Map<string, Promise<string>>();
// Lottie 已解析地址缓存。
const lottieResolvedSrcCache = new Map<string, string>();
// 判断是否 .lottie 文件。
const isDotLottieFile = (src: string) => /\.lottie(?:$|\?)/i.test(src);
// 解析后资源载荷类型。
type LottieSourcePayload =
  | { type: 'dotlottie'; buffer: ArrayBuffer }
  | { type: 'blob'; blob: Blob };
// ArrayBuffer 转 base64。
const toBase64 = (buffer: ArrayBuffer) => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    for (let j = 0; j < chunk.length; j += 1) {
      binary += String.fromCharCode(chunk[j]);
    }
  }
  return window.btoa(binary);
};

// 解析 Lottie 资源地址（支持远端转 object/data URL）。
const resolveLottieSrc = (src: string) => {
  if (typeof window === 'undefined') {
    return Promise.resolve(src);
  }
  if (!/^https?:\/\//i.test(src)) {
    return Promise.resolve(src);
  }
  const cachedPromise = lottieSrcPromiseCache.get(src);
  if (cachedPromise) {
    return cachedPromise;
  }
  const cachedResolvedSrc = lottieResolvedSrcCache.get(src);
  if (cachedResolvedSrc) {
    return Promise.resolve(cachedResolvedSrc);
  }

  const requestPromise: Promise<string> = fetch(src)
    .then(async (res): Promise<LottieSourcePayload> => {
      if (!res.ok) {
        throw new Error(`Fetch lottie failed: ${res.status}`);
      }
      if (isDotLottieFile(src)) {
        const buffer = await res.arrayBuffer();
        return { type: 'dotlottie', buffer };
      }
      const blob = await res.blob();
      return { type: 'blob', blob };
    })
    .then((payload) => {
      if (payload.type === 'dotlottie') {
        const base64 = toBase64(payload.buffer);
        const dataUrl = `data:application/octet-stream;base64,${base64}`;
        lottieResolvedSrcCache.set(src, dataUrl);
        return dataUrl;
      }
      const objectUrl = URL.createObjectURL(payload.blob);
      lottieResolvedSrcCache.set(src, objectUrl);
      return objectUrl;
    })
    .catch(() => src)
    .finally(() => {
      lottieSrcPromiseCache.delete(src);
    });

  lottieSrcPromiseCache.set(src, requestPromise);
  return requestPromise;
};

// 预加载一组 Lottie 资源。
export const preloadDotLottieAssets = (srcList: string[]) => {
  const dedupList = Array.from(new Set(srcList.filter(Boolean)));
  return Promise.all(dedupList.map((src) => resolveLottieSrc(src)));
};

// DotLottie 组件入参类型。
export interface IDotLottie {
  className?: string;
  src: string;
  autoplay?: boolean;
  loop?: boolean;
  onComplete?: () => void;
  // 将运行时和资源就绪状态通知给父组件，用于决定是否隐藏静态图标。
  onReadyStateChange?: (ready: boolean) => void;
}

// DotLottie ref 暴露的方法类型。
export interface IDotLottieMethods {
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onSeek: (frame: number) => void;
}

// DotLottie 组件定义。
const DotLottie = forwardRef(({
  className,
  src,
  autoplay = true,
  loop = true,
  onComplete,
  onReadyStateChange,
}: IDotLottie, ref) => {
  // 暴露给父层的控制方法。
  useImperativeHandle(ref, () => ({
    onPlay: play,
    onPause: pause,
    onStop: stop,
    onSeek: seek,
  }));

  const [dotLottie, setDotLottie] = useState<any>();
  const [DotLottieReact, setDotLottieReact] = useState<any>(null);
  const [resolvedSrc, setResolvedSrc] = useState<string>('');

  // 获取 dotLottie 实例回调。
  const dotLottieRefCallback = (dotLottie: any) => {
    setDotLottie(dotLottie);
  };

  // 播放动画。
  const play = () => {
    const isLoaded = dotLottie?.isLoaded;
    const isReady = dotLottie?.isReady;
    if (isLoaded && isReady) {
      dotLottie?.play();
    }
    return isLoaded && isReady;
  }

  // 暂停动画。
  const pause = () => {
    dotLottie?.pause();
  }

  // 停止动画。
  const stop = () => {
    dotLottie?.stop();
  }

  // 跳到指定帧。
  const seek = (frame: number) => {
    dotLottie?.setFrame(frame);
  }

  // 懒加载 DotLottie runtime。
  useEffect(() => {
    if (!isRC) {
      preloadDotLottieRuntime().then(module => {
        if (!module) {
          return;
        }
        setDotLottieReact(() => module.DotLottieReact);
      });
    }
  }, []);

  // 根据 src 解析 object URL。
  useEffect(() => {
    let cancelled = false;
    if (!src) {
      setResolvedSrc('');
      return () => {
        cancelled = true;
      };
    }
    waitForLottiePreload()
      .then(() => getLottieObjectUrl(src))
      .then((objectUrl) => {
        if (!cancelled) {
          setResolvedSrc(objectUrl || '');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResolvedSrc('');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [src]);

  // 监听动画 complete 事件。
  useEffect(() => {
    if (dotLottie) {
      dotLottie.addEventListener('complete', onComplete);
    }

    return () => {
      if (dotLottie) {
        dotLottie.removeEventListener('complete', onComplete);
      }
    };
  }, [dotLottie, onComplete]);

  useEffect(() => {
    // 只要运行时和资源地址任一未就绪，都通知父组件继续保留静态图标。
    onReadyStateChange?.(Boolean(DotLottieReact && resolvedSrc));
  }, [DotLottieReact, resolvedSrc, onReadyStateChange]);

  // runtime 或资源未就绪时不渲染。
  if (!DotLottieReact || !resolvedSrc) {
    return null;
  }

  return (
    <div className={cx(Styles.root, className)}>
      <DotLottieReact
        className={Styles.lottie}
        src={resolvedSrc}
        autoplay={autoplay}
        loop={loop}
        dotLottieRefCallback={dotLottieRefCallback}
      />
    </div>
  );
});

// 设置组件显示名。
DotLottie.displayName = 'DotLottie';

// 导出组件。
export default DotLottie;
